import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { WrappedDropdown } from '@blueprint/dropdown';
import { Label } from '@blueprint/label';
import { TextArea, TextField } from '@blueprint/text-input';
import { Checkbox } from '@blueprint/checkbox';
import {
    DropdownWrapper,
    EmailAgentStyledForm,
    ErrorMessage,
    TermsText,
    CaptchaWrapper
} from './EmailAgentForm.styles';
import { Button } from '@blueprint/ui';
import { Link } from '@blueprint/link';
import EmailAgentAvailabilityForm from './EmailAgentAvailabilityForm/EmailAgentAvailabilityForm';
import { EmailAgentSubmissionProps } from './EmailAgentForm.types';

import { DeepMap, FieldError, useForm } from 'react-hook-form/dist/index.ie11';
import validationDecoration from 'shared/helpers/validationDecoration';
import {
    EmailLeadFormFields,
    EmailLeadResponse,
    ListingAnalyticsEcommerce,
    ListingAnalyticsTaxonomy,
    Maybe
} from 'graphql.generated';
import {
    getDefaultValue,
    getMemberType,
    getPromoType
} from './EmailAgentForm.helpers';
import { CONTACT_FOR_SALE_SITUATION_KEYS } from './constants/userSituationKeys';
import { useCaptcha, useErrorTracking } from './hooks';
import { ListingSection } from 'listing-details/types';
import {
    trackShowCaptcha,
    trackSubmissionErrors
} from './EmailAgentForm.tracking';
import { useMutation } from '@apollo/client';
import { SEND_EMAIL_AGENT_LEAD } from './EmailAgentForm.mutation';
import { TABLET_BREAKPOINT } from 'listing-details/constants';
import {
    EMAIL_REGEX,
    NAME_REGEX,
    PHONE_REGEX,
    POSTCODE_REGEX
} from 'shared/regex/userDetails';
import useIsWidthLessThanBreakpoint from 'shared/hooks/app/useIsWidthLessThanBreakpoint';
import { trackEvent } from 'shared/helpers/segment/trackEvent';
import { Ecommerce, EventNames } from 'shared/tracking/contracts';

const senderPropertyStatusOptions: Record<string, string> = {
    first_time_buyer: 'I am a first-time buyer',
    looking_to_invest: "I am an investor/I'm hoping to invest",
    no_property_to_sell: 'I do not have a property to sell',
    offer_on_own_property: 'I have an offer on my property',
    other: 'Other',
    property_to_let: 'I have a property to let',
    property_to_sell: 'I have a property to sell',
    recently_sold: 'I have recently sold'
};

type EmailAgentFormProps = {
    formFields?: Maybe<EmailLeadFormFields>;
    section: ListingSection;
    listingId: number;
    showCaptcha: boolean;
    successUri?: Maybe<string>;
    searchIdentifier?: string | null | undefined;
    analytics: {
        analyticsEcommerce: Pick<
            ListingAnalyticsEcommerce,
            'category' | 'id' | 'variant'
        >;
        analyticsTaxonomy: Pick<
            ListingAnalyticsTaxonomy,
            'memberType' | 'branchId' | 'branchName'
        >;
    };
};

const DynamicCaptcha = dynamic(
    () => import('listing-details/components/Captcha/Captcha'),
    {
        ssr: false
    }
);
const TermsBlock = () => {
    return (
        <TermsText>
            By submitting this form, you accept our{' '}
            <Link href="/terms/">Terms of Use</Link> and{' '}
            <Link href="/privacy/">Privacy Policy</Link>.
        </TermsText>
    );
};

const EmailAgentForm = ({
    formFields,
    section,
    listingId,
    showCaptcha,
    successUri,
    searchIdentifier,
    analytics: { analyticsEcommerce, analyticsTaxonomy }
}: EmailAgentFormProps) => {
    const [isTablet] = useIsWidthLessThanBreakpoint([TABLET_BREAKPOINT]);

    useEffect(() => {
        if (showCaptcha) {
            trackShowCaptcha();
        }
    }, [showCaptcha]);

    const isForSale = ['for-sale', 'new-homes'].includes(section);
    const isNewHome = section === 'new-homes';

    const listingSuccessUrl: string | boolean = (() => {
        const url = successUri;

        if (!url) {
            return false;
        }

        if (searchIdentifier) {
            return `${url}?search_identifier=${searchIdentifier}`;
        }

        return url;
    })();

    // track submit event in segment
    const triggerSegmentTracking = () => {
        const ecommerce: Ecommerce = {
            leadMedium: 'email',
            leadFocus: 'listing_lead',
            leadType: 'applicant',
            leadCategory: analyticsEcommerce.category,
            memberType: getMemberType(analyticsTaxonomy.memberType),
            products: [
                {
                    product_id: analyticsEcommerce.id.toString(),
                    listing_id: listingId.toString(),
                    branch_id:
                        analyticsTaxonomy.branchId?.toString() || undefined,
                    branch_name: analyticsTaxonomy.branchName,
                    promotion_type: getPromoType(analyticsEcommerce.variant)
                }
            ]
        };

        trackEvent(EventNames.LISTING_LEAD_SENT, undefined, ecommerce);
    };

    // setup toggle for availability selector
    const [interestIsChecked, setInterestChecked] = useState(false);
    const [selectedAvailableDates, setSelectedAvailableDates] = useState<
        Array<string>
    >();

    // set errorMessage above submit button
    const [hasSubmissionError, setHasSubmissionError] = useState(false);

    // setup lead form
    const form = useForm<EmailAgentSubmissionProps>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
        criteriaMode: 'all'
    });

    const {
        errors,
        formState: { isSubmitting, touched },
        handleSubmit,
        register
    } = form;

    const { onCaptchaChange } = useCaptcha(
        showCaptcha,
        form,
        'Please complete the captcha'
    );

    useErrorTracking(errors);

    const formInputProps = (
        field: Extract<keyof EmailAgentSubmissionProps, string>
    ) => ({
        id: field,
        name: field,
        validationErrorMessage: (errors[field] as FieldError)?.message,
        validationStatus: validationDecoration<EmailAgentSubmissionProps>(
            field,
            errors,
            touched
        )
    });

    const submitForm = (formData: EmailAgentSubmissionProps) => {
        setHasSubmissionError(false);
        triggerSegmentTracking();
        sendEmailAgentMutation({
            variables: {
                id: listingId,
                lead: {
                    ...formData,
                    availability: selectedAvailableDates,
                    interest: interestIsChecked,
                    searchIdentifier: searchIdentifier
                        ? searchIdentifier
                        : undefined
                }
            }
        });
    };

    const formErrorHandler = (
        errors: DeepMap<EmailAgentSubmissionProps, FieldError>
    ) => {
        const errorArray: Array<string> = [];
        for (const v of Object.values(errors)) {
            if (v?.message) {
                errorArray.push(v.message);
            }
        }
        trackSubmissionErrors(errorArray);
    };

    const [sendEmailAgentMutation] = useMutation<{
        createEmailLead: EmailLeadResponse;
    }>(SEND_EMAIL_AGENT_LEAD, {
        onCompleted: ({ createEmailLead: { success } }) => {
            if (success && listingSuccessUrl) {
                window.location.assign(listingSuccessUrl);
            }
        },
        onError: () => setHasSubmissionError(true)
    });

    return (
        <EmailAgentStyledForm
            onSubmit={handleSubmit(submitForm, formErrorHandler)}
            data-testid="email-agent-form"
            noValidate
        >
            <TextField
                isRequired
                type="text"
                label="Full name"
                defaultValue={getDefaultValue(formFields?.name)}
                ref={register({
                    pattern: {
                        value: NAME_REGEX,
                        message:
                            'Please enter both your first and last names e.g. John Smith'
                    },
                    required: 'Please enter your full name'
                })}
                {...formInputProps('name')}
            />

            <TextField
                isRequired
                type="email"
                label="Email address"
                ref={register({
                    pattern: {
                        value: EMAIL_REGEX,
                        message:
                            'Please provide a full, valid email address e.g. johnsmith@gmail.com'
                    },
                    required: 'Please enter an email address'
                })}
                defaultValue={getDefaultValue(formFields?.email)}
                {...formInputProps('email')}
            />

            <TextField
                isRequired
                type="tel"
                label="Phone number"
                ref={register({
                    pattern: {
                        value: PHONE_REGEX,
                        message:
                            'Please provide a full, valid phone number - include a + before international numbers'
                    },
                    required: 'Please enter a phone number'
                })}
                defaultValue={getDefaultValue(formFields?.phone)}
                {...formInputProps('phone')}
            />

            {isForSale && (
                <>
                    <TextField
                        isRequired
                        type="text"
                        label="Your postcode"
                        ref={register({
                            pattern: {
                                value: POSTCODE_REGEX,
                                message:
                                    'Please provide a full, valid postcode e.g. AB1 2CD'
                            },
                            required: 'Please enter a postcode'
                        })}
                        defaultValue={getDefaultValue(formFields?.postcode)}
                        {...formInputProps('postcode')}
                    />

                    <Label htmlFor="senderPropertyStatus" isRequired>
                        Your situation
                    </Label>
                    <DropdownWrapper isError={!!errors.senderPropertyStatus}>
                        <WrappedDropdown
                            defaultValue={
                                getDefaultValue(formFields?.status) || ''
                            }
                            ref={register({
                                required:
                                    'Please select the option that best reflects your status'
                            })}
                            errorText={errors.senderPropertyStatus?.message}
                            {...formInputProps('senderPropertyStatus')}
                        >
                            <option value="">Please select</option>
                            {CONTACT_FOR_SALE_SITUATION_KEYS.map(
                                (situationKey) => (
                                    <option
                                        key={situationKey}
                                        value={situationKey}
                                    >
                                        {
                                            senderPropertyStatusOptions[
                                                situationKey
                                            ]
                                        }
                                    </option>
                                )
                            )}
                        </WrappedDropdown>
                    </DropdownWrapper>
                </>
            )}

            <TextArea
                isOptional
                isCharacterCounter
                label="Your message"
                maximumCharacterCount={200}
                ref={register({ maxLength: 200 })}
                {...formInputProps('message')}
            />

            <Checkbox
                hasBorder
                label="I would be interested in viewing this property"
                checked={interestIsChecked}
                onChange={() => setInterestChecked(!interestIsChecked)}
                {...formInputProps('interest')}
            />

            {!isNewHome && interestIsChecked && (
                <EmailAgentAvailabilityForm
                    startDate={new Date()}
                    onChange={(availabilityWindows) =>
                        setSelectedAvailableDates(availabilityWindows)
                    }
                    selectedAvailabilities={selectedAvailableDates}
                />
            )}

            {isTablet && <TermsBlock />}

            {showCaptcha && (
                <CaptchaWrapper data-testid="email-agent-captcha">
                    <DynamicCaptcha
                        onChange={onCaptchaChange}
                        error={errors.captchaToken?.message || null}
                    />
                </CaptchaWrapper>
            )}

            {hasSubmissionError && (
                <ErrorMessage data-testid="error-message">
                    There was a problem submitting this form.
                    <br />
                    Please try again.
                </ErrorMessage>
            )}

            <Button type="submit" value="Submit" disabled={isSubmitting}>
                Send enquiry
            </Button>

            {!isTablet && <TermsBlock />}
        </EmailAgentStyledForm>
    );
};

export default EmailAgentForm;
